<template>
  <div aria-live="polite" aria-atomic="true" class="toast-container">
    <TransitionGroup name="toast" tag="div" appear>
      <div
        v-for="item in messages"
        :key="item.id"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        :class="'bg-' + type"
        @mouseover="pauseProgress(item)"
        @mouseout="resumeProgress(item)"
      >
        <div
          v-if="item.timeOut > 0 && item.showProgress"
          style="height: 6px; border-radius: 20px; width: inherit"
        >
          <div
            :class="'bg-' + item.type"
            :style="{
              width: getCurrentProgress(item) - 1.25 + '%',
              marginLeft: '-4px',
            }"
          />
        </div>

        <div style="height: 35px">
          <span class="content">
            <img class="toast-icon" :src="getImg()" />
            <div
              class="toast-body"
              :style="{
                color: getColor(),
                marginTop: item.message.length > 15 ? '2px' : '15px',
              }"
            >
              {{ item.message }}
            </div>
          </span>
          <button
            v-if="item.closeable"
            type="button"
            data-dismiss="toast"
            aria-label="Close"
            @click="remove(item)"
            class="close-button"
          >
            <img src="../assets/simple-close.png" width="10px" height="10px" />
          </button>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script>
import { EventBus } from "../event-bus";

export default {
  name: "SToast",
  props: {
    showProgress: {
      type: Boolean,
      default: true,
    },
    rtl: {
      type: Boolean,
      default: true,
    },
    maxMessages: {
      type: Number,
      default: 5,
    },
    timeOut: {
      type: Number,
      default: 4000,
    },
    closeable: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
    },
    type: {
      type: String,
      default: "success",
    },
    hidden: {
      type: Function,
    },
  },
  data() {
    return {
      messages: [],
      count: 0,
    };
  },
  created() {
    // start listening for "toast-message" channel's events
    EventBus.$on("toast", this.receiveEvent);

    if (this.count >= 0) {
      this.initialize();
    }
  },
  methods: {
    initialize() {
      if (this.message) {
        let obj = {
          options: {
            timeOut: this.timeOut,
          },
          message: this.message,
          type: this.type,
        };
        this.receiveEvent(obj);
      }
    },
    /**
     * Showimg img according to type
     */
    getImg() {
      if (this.type == "success") {
        return require("../assets/success-white.png");
      } else if (this.type == "warning") {
        return require("../assets/warning.png");
      } else if (this.type == "error") {
        return require("../assets/error-white.png");
      }
    },
    /**
     * Setting color according to type
     */
    getColor() {
      if (this.type == "success") {
        return "white";
      } else if (this.type == "warning") {
        return "#272727";
      } else if (this.type == "error") {
        return "#f4f4f4";
      }
    },
    /**
     * Sending parameters from received event
     */
    receiveEvent(e) {
      this.handleMessageEvent(e.message, e.type, e.options);
    },
    /**
     * Handle a message event.
     */
    handleMessageEvent(message, type, options = {}) {
      if (message) {
        this.addMessage(message, type, options);
      }
    },
    /**
     * Add a message to the list.
     */
    addMessage(message, type, options = {}) {
      // mapping event parameters in props
      (this.showProgress = options.showProgress ? options.showProgress : true),
        (this.rtl = options.rtl ? options.rtl : true),
        (this.timeOut = options.timeOut ? options.timeOut : 4000),
        (this.closeable = options.closeable ? options.closeable : false);
      this.type = type;

      // message object data
      var messageData = {
        id: this.count++,
        message: message,
        type: this.type,
        showProgress: this.showProgress,
        timeOut: this.timeOut,
        closeable: this.closeable,
        rtl: this.rtl,
        progress: 0,
        paused: false,
        animationFrame: null,
      };

      // merge custom options
      messageData = Object.assign({}, messageData, options);

      // prepend new message to front of messages's array
      this.messages.unshift(messageData);

      // check if maximum messages to store has been reached
      if (this.messages.length > this.maxMessages) {
        // remove from the end
        this.messages.splice(this.maxMessages);
      }
      if (messageData.timeOut > 0) {
        // start message timeout
        this.startTimeout(messageData);
      }
    },
    startTimeout(messageData, startFrom = 0) {
      const self = this;
      performance.now(); //will give more precision to be used for a smooth progress bar
      const startTime = performance.now();
      const start = () => {
        // start animation calculating
        messageData.animationFrame = requestAnimationFrame((timestamp) => {
          const timeElapsed = timestamp + startFrom - startTime;
          // check if is message object already was removed
          // or progress a toast message was hovered on
          if (!self.exists(messageData) || messageData.paused) {
            // cancel the animation
            cancelAnimationFrame(messageData.animationFrame);
            return;
            // check if timout value has not been reached
          } else if (timeElapsed < messageData.timeOut) {
            // calcalute the progress
            const progress = timeElapsed / messageData.timeOut;
            // set a new progress
            messageData.progress = progress;
            // continue calculating the progress
            start();
          } else {
            // timout has been reached
            messageData.progress = 1;
            // cancel the animation
            cancelAnimationFrame(messageData.animationFrame);
            // remove message from array
            self.remove(messageData);
            /* Emit hidden when timeout has been reached */
            this.$emit("hidden");
            return;
          }
        });
      };
      start();
    },
    /**
     * Pause progress bar when hovering on a toast.
     */
    pauseProgress(messageData) {
      if (messageData.timeOut <= 0) return;
      messageData.paused = true;
    },
    /**
     * Resume progress bar when going a way from 'mouseover' status.
     */
    resumeProgress(messageData) {
      if (messageData.timeOut <= 0) return;
      messageData.paused = false;
      // re-start animation calculating with a given start point
      this.startTimeout(
        messageData,
        messageData.timeOut * messageData.progress
      );
    },
    /**
     * Get current message progress.
     */
    getCurrentProgress(messageData) {
      // set progress to "100" when progress value is close to 100.
      // why? sometimes when a progress is someting like (99.899)
      // it will show a little empty space at the end of progress
      // bar which doesnt get filled.
      if (Math.round(messageData.progress * 100) === 100) {
        return 100;
      }
      return messageData.progress * 100;
    },
    /**
     * Remove a message from the list.
     */
    remove(messageData) {
      const index = this.messages.findIndex(
        (message) => message.id === messageData.id
      );
      this.messages.splice(index, 1);
    },
    /**
     * Remove a message from the list.
     */
    exists(messageData) {
      const index = this.messages.findIndex(
        (message) => message.id === messageData.id
      );
      if (index === -1) {
        return false;
      }
      return true;
    },
  },
};
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 15%;
  right: 20px;
  min-width: 350px;
  z-index: 1090;
}
.toast-icon {
  position: relative;
  width: 22px;
  height: 22px;
  right: 2px;
  transform: translateY(-50%);
  top: 18px;
  float: left;
  left: 12px;
}
.icon-right {
  right: 10px;
  top: 26px;
}
.icon-left {
  left: 10px;
  top: 26px;
}
.toast-enter {
  opacity: 0.5 !important;
}
.toast-enter-active {
  transition: opacity 0.5s ease-in;
}
.toast-enter-to {
  opacity: 1;
}
.toast-leave {
  opacity: 0.5;
}
.toast-leave-active {
  opacity: 0;
  transition: opacity 0.5s ease-out;
}
.toast-leave-to {
  opacity: 0;
}
.bg-success {
  background-color: #a4e600;
  min-height: 50px;
  border-radius: 20px;
  padding: 6px;
}
.bg-error {
  background-color: rgba(243, 87, 87, 0.993);
  min-height: 50px;
  border-radius: 20px;
  padding: 6px;
}
.bg-warning {
  background-color: rgb(240, 175, 54);
  min-height: 50px;
  border-radius: 20px;
  padding: 6px;
}
.close-button {
  background-color: transparent;
  border: none;
  float: right;
  position: relative;
  top: -15px;
  left: -10px;
}
.toast-body {
    font-family: "Roboto !important";
  font-size: 14px;
  align-self: self-end;
  color: #272727;
  white-space: pre-wrap;
  margin-left: 10px;
}
.content {
  display: grid;
  grid-template-columns: 10% 80% 10%;
  grid-column-gap: 5px;
  padding-top: 6px;
}
</style>